// Copyright (c) Docugami, Inc. All rights reserved.

import { lightHighlightBlue } from "components/shell/presentation/AppStyles";
import { useEffect, useMemo, useState } from "react";
import { createSelectLabelId, folderIdSelector, selectIsDgmlInitialized, selectSpansIndexedByLabel } from "store/dgml/dgmlSelectors";
import { selectAllEntries, setLabelIsEditing, useGetBindingsQuery } from "store/reports/reportsSlice";
import { selectEntries, selectReportEntryBindings, selectReportId, selectSelectedLabelState } from "store/reports/selectors";
import { removePreviewChunkSet, setPreviewChunks } from "store/sdvSlice";
import { useAppDispatch, useAppSelector } from "./redux";
import { arrayEquals } from "utils/ArrayUtils";

interface ILabelChunks {
    label: string | undefined;
    labelId?: string;
    isEditing: boolean;
    chunks: string[];
}

const selectLabelId = createSelectLabelId();
// Returns a live view of the selected label's chunks
export function useLabelChunks(documentId: string, multiSelectEnabled: boolean): ILabelChunks {
    const [targetSpanIds, setTargetSpanIds] = useState<string[]>([]);

    const { clusterId, fileId } = folderIdSelector(documentId) ?? {};
    const selectedLabel = useAppSelector(selectSelectedLabelState);
    const labelId = useAppSelector(state => selectLabelId(state, clusterId ?? '', selectedLabel?.label ?? ''));

    // Report-specific state
    const reportId = useAppSelector(selectReportId);
    useGetBindingsQuery({ reportId, fileId: fileId ?? '' });
    const reportEntries = useAppSelector(selectEntries);
    const labelEntryId = useMemo(() => !!reportEntries ? selectAllEntries(reportEntries).find(x => x.labelId === labelId)?.id : undefined, [reportEntries, labelId])
    const documentEntryBindings = useAppSelector(state => selectReportEntryBindings(state, fileId)?.entities[labelEntryId ?? '']);
    const entryBindings = useMemo(() => documentEntryBindings?.bindings.map(x => x.spanId!) as string[] ?? undefined, [documentEntryBindings])

    const spanIdsLookup = useAppSelector(state => {
        if (selectIsDgmlInitialized(state, clusterId ?? '', fileId ?? '')) {
            return selectSpansIndexedByLabel(state, clusterId ?? '', fileId ?? '');
        }
    });

    useEffect(() => {
        if (!selectedLabel?.label || !documentId) {
            setTargetSpanIds([]);
            return;
        }

        let spanIds: string[] = [];
        // Use the entry bindings associated with this label id if any
        if (!!entryBindings) {
            spanIds = entryBindings;
        }
        else if (!!spanIdsLookup) {
            spanIds = spanIdsLookup[selectedLabel.label];
        }

        spanIds = Array.from(new Set(spanIds));
        setTargetSpanIds(currentSpanIds => (!currentSpanIds || !arrayEquals(currentSpanIds, spanIds)) ? spanIds : currentSpanIds);
    }, [documentId, selectedLabel?.label, spanIdsLookup, entryBindings])

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLabelIsEditing(!!multiSelectEnabled));
    }, [multiSelectEnabled, dispatch])

    // Set the canvas display chunks for the selected label
    useEffect(() => {
        if (!multiSelectEnabled && !!targetSpanIds.length) {
            dispatch(setPreviewChunks({
                id: 'selected-label',
                chunks: { spanIds: targetSpanIds, color: lightHighlightBlue }
            }))
        }
        else {
            dispatch(removePreviewChunkSet('selected-label'));
        }
    }, [dispatch, selectedLabel, multiSelectEnabled, targetSpanIds])

    return {
        label: selectedLabel?.label,
        labelId: selectedLabel?.id,
        isEditing: !!selectedLabel?.isEditing,
        chunks: targetSpanIds
    }
}